<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import OmniDialog from "@/components/omni/OmniDialog.vue";
import { useOrderExpenses } from "@/composables";

import type { OrderExpense } from "@/api/orders/dto/order.dto";

const { t } = useI18n();
const { isLoading, deleteOrderExpense, formatOrderExpenseRate } = useOrderExpenses();

const showDialog = defineModel<boolean>({ default: false });

const props = defineProps<{
  orderId: string;
  expense: OrderExpense | null;
}>();

const emit = defineEmits<{
  (e: "success", expense: OrderExpense): void;
}>();

const confirmationText = computed<string>(() => {
  if (!props.expense) {
    return "";
  }

  return t("order-expenses.delete-confirmation", {
    type: t(`order-expenses.${props.expense.type}`),
    rate: formatOrderExpenseRate(props.expense.rate),
  });
});

function close() {
  showDialog.value = false;
}

async function removeExpense() {
  if (!props.expense) {
    return;
  }

  const isDeleted = await deleteOrderExpense(props.orderId, props.expense.id);

  if (!isDeleted) {
    return;
  }

  emit("success", props.expense);
  close();
}
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :primary-action-label="t('general.delete')"
    :primary-button-loading="isLoading"
    :secondary-action-label="t('general.cancel')"
    :secondary-button-disabled="isLoading"
    :title="t('payments.delete-expense')"
    @primary-action:click="removeExpense"
    @secondary-action:click="close"
  >
    <div class="text-grey-darken-3">{{ confirmationText }}</div>
  </omni-dialog>
</template>
